/**
 * The colour tokens a status pill renders: foreground, soft fill, border.
 *
 * One shared shape for every status→tone map (job / site / client), so the
 * pill's `tone` prop and each module's `*_STATUS_TONE` record agree without
 * redeclaring the interface per module.
 */
export interface StatusTone {
  /** Foreground / accent color. */
  readonly color: string;
  /** Soft background tint. */
  readonly bg: string;
  /** Border color. */
  readonly border: string;
}

/**
 * A pill tone mixed over one colour token: the token as text, a 12% fill and a
 * 35% border. A mix rather than a literal `rgba` so the fill follows the token
 * when `html.light` redefines it — see {@link INACTIVE_TONE}.
 */
export function mixTone(color: string): StatusTone {
  return {
    color,
    bg: `color-mix(in srgb, ${color} 12%, transparent)`,
    border: `color-mix(in srgb, ${color} 35%, transparent)`,
  };
}

/**
 * Tone for a live record's "Active" marker — project, site, client, anything
 * else that is in use for new work.
 *
 * `color-mix` over `--dyna-ok` for the same reason {@link INACTIVE_TONE} mixes
 * over `--dyna-ink-dim`: that token is redefined under `html.light` (`#34d399`
 * → `#3f9e73`), so the literal `rgba(52,211,153,…)` spelling this replaces let
 * the pill's text follow the theme while its fill stayed at the dark green.
 *
 * NOTE — this is deliberately NOT a pixel-for-pixel port. The literal was 10%
 * fill / 30% border; these are 12% / 35%, matching {@link INACTIVE_TONE}, so the
 * active and inactive pills in a registry list carry the same visual weight
 * instead of the active one sitting a shade lighter. Every Active pill (sites,
 * clients) is marginally stronger than before.
 */
export const ACTIVE_TONE: StatusTone = mixTone('var(--dyna-ok)');

/**
 * Tone for a deactivated record's "Inactive" marker — project, site, anything
 * else that gets switched off rather than deleted.
 *
 * Deliberately muted: an inactive record is a quiet state, not an error, and
 * the Active toggle's hint promises it stays visible.
 *
 * Expressed as `color-mix` over `--dyna-ink-dim` rather than a literal `rgba`,
 * because that token is redefined under `html.light`. The literal spelling of
 * this tone that used to sit in the site/job/client display modules baked in
 * the DARK value (`#74747a`) for `bg` and `border` while leaving `color` as the
 * token — so in light mode the pill's text followed the theme and its fill did
 * not. Keep it a mix, not a constant.
 */
export const INACTIVE_TONE: StatusTone = mixTone('var(--dyna-ink-dim)');

/**
 * Row opacity for a deactivated record — the row-level counterpart of
 * {@link INACTIVE_TONE}.
 *
 * A deactivated project or site is dimmed rather than hidden: it keeps its data
 * and history and stays in the registry, so the reader needs to see it, just
 * not mistake it for live. Applied through `inactiveRowStyle` (`@/lib/activity`),
 * so every registry row dims a switched-off record the same way.
 */
export const DEACTIVATED_ROW_OPACITY = 0.55;
